<template>
  <NoteSection
    label="Review of Systems"
    required
    :highlight-required="isSectionIncomplete"
    :form-validated="formValidated"
  >
    <text-area
      v-model="systems_review"
      class="col-sm-12"
      label="Enter review of systems..."
      required
      ai
      counter="1300"
      maxlength="1300"
      word-count="8"
      @click:prepend="generateSystemsReview"
      @input="$emit('input', systems_review)"
    ></text-area>
  </NoteSection>
</template>

<script>
export default {
  props: {
    value: {
      type: String,
      default: null,
    },
    medicalHistoryList: {
      type: Array,
      default: () => [],
    },
    medicalHistoryOther: {
      type: String,
      default: null,
    },
    riskFactorList: {
      type: Array,
      default: () => [],
    },
    riskFactorOther: {
      type: String,
      default: null,
    },

    /*
    patientDob: {
      type: String,
      default: null,
    },
    patientGender: {
      type: String,
      default: 'person',
    },
    therapeuticAnticoagulant: {
      type: Boolean,
      default: false,
    },
    */
    neuropathy: {
      type: Boolean,
      default: false,
    },
    neuropathyType: {
      type: String,
      default: null,
    },
    neuropathySeverity: {
      type: String,
      default: null,
    },
    neuropathyTypeTwo: {
      type: String,
      default: null,
    },
    neuropathySeverityTwo: {
      type: String,
      default: null,
    },
    neuropathyTypeThree: {
      type: String,
      default: null,
    },
    neuropathySeverityThree: {
      type: String,
      default: null,
    },
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      systems_review: this.value,
    }
  },
  computed: {
    /*
    age() {
      return this.$custom.ageInYears(this.patientDob)
    },
    */
    // Evaluates if the systems review text field is missing required input
    isSectionIncomplete() {
      return !this.systems_review || !this.systems_review.trim()
    },
  },
  watch: {},
  methods: {
    generateSystemsReview() {
      try {      
        let systemsReview = ''

        const medHistoryAndRiskFactorList = [
          ...(this.medicalHistoryList || []),
          ...(this.riskFactorList || [])
        ]

        const categoryGroups = medHistoryAndRiskFactorList.reduce((groups, item) => {
          const categories = (typeof item.category === 'string' && item.category.length)
            ? item.category.split(',').map(c => c.trim()).filter(Boolean)
            : ['Other']

          categories.forEach(cat => {
            if (!groups[cat]) groups[cat] = []
            if (item.title) groups[cat].push(item.title)
          })

          return groups
        }, { Other: [] })

        if (this.neuropathy) {
          const neuropathyPairs = [
            { type: this.neuropathyType, severity: this.neuropathySeverity },
            { type: this.neuropathyTypeTwo, severity: this.neuropathySeverityTwo },
            { type: this.neuropathyTypeThree, severity: this.neuropathySeverityThree },
          ]

          const descriptors = neuropathyPairs
            .filter(p => p.type && p.severity)
            .map(p => `${p.severity.toLowerCase()} ${p.type.toLowerCase()}`)

          const unique = [...new Set(descriptors)]

          if (unique.length) {
            if (!categoryGroups.Neuro) categoryGroups.Neuro = []
            const noun = unique.length === 1 ? 'neuropathy' : 'neuropathies'
            const joined = this.$custom.toListCommaAnd
              ? this.$custom.toListCommaAnd(unique)
              : unique.join(', ')
            categoryGroups.Neuro.push(`${joined} ${noun}`)
          }
        }

        if (this.medicalHistoryOther) categoryGroups.Other.push(this.medicalHistoryOther)
        if (this.riskFactorOther) categoryGroups.Other.push(this.riskFactorOther)

        const order = ['Pulmonary', 'Cardiac', 'Vascular', 'Neuro', 'Immuno']
        if (categoryGroups.Other.length > 0) order.push('Other')

        const parts = order.map(category => {
          const items = categoryGroups[category] || []
          const list = items.length
            ? this.$custom.toListComma(items, null, 'none')
            : 'None'
          return `${category}: ${list}`
        })

        systemsReview = parts.join(', ')

        this.systems_review = this.$custom.clean(systemsReview, 'Systems Review')
        this.$emit('input', this.systems_review)

      } catch (err) {
        console.error('Error generating systems review:', err)
        this.displayMsg(`There was an error trying to generate the Systems Review. Please try again. \nError: ${err}`, 'error')
      }
    },

    /* Old text generation
    generateSystemsReview() {
      let systemsReview = ''
      const systemItemTitles = []
      if (this.medicalHistoryList) {
        this.medicalHistoryList.forEach(medicalHistory => {
          if (medicalHistory.title !== undefined) {
            systemItemTitles.push(medicalHistory.title)
          }
        })
      }
      const riskFactorTitles = []
      if (this.riskFactorList) {
        this.riskFactorList.forEach(riskFactor => {
          if (riskFactor.title !== undefined) {
            riskFactorTitles.push(riskFactor.title)
            if (['Atrial fibrillation', 'CAD', 'CHF', 'CVA/Stroke', 'Anemia', 'Hypertension', 'Obesity', 'COPD', 'Respiratory Failure', 'CKD', 'PAD', 'PVD', 'POD', 'Diabetes Type 1', 'Diabetes Type 2', 'Renal Failure', 'Hyperlipidemia', 'VSD', "Alzheimer's", 'Cognitive Impairment', 'Dementia', 'Depression', 'Dysphagia', 'Paraplegia', 'Quadriplegia', 'Spasticity', 'TBI', 'CVA/Stroke', 'Encephalopathy', 'Traumatic Brain Injury', 'Hemiplegia'].includes(riskFactor.title)) {
              systemItemTitles.push(riskFactor.title)
            }
          }
        })
      }
      systemsReview = this.$custom.randomString(['This', 'The patient', 'The treated patient', 'The patient examined', 'The patient seen', 'The patient observed', 'The patient under observation'])
      systemsReview += 'is '
      systemsReview += this.$custom.articleAge(this.age)
      if (this.patientGender) {
        systemsReview += this.$custom.toLowerCase(this.patientGender)
        systemsReview += ' '
      }
      if (systemItemTitles.length > 0) {
        systemsReview += this.$custom.randomString(['with the following diagnoses and risk factors:', 'who has the following diagnoses and risk factors:', 'having diagnoses and risk factors as follows:', 'who was found to have the following risk factors and diagnoses:', 'for whom the following diagnoses/risk factors were found:', 'for whom the following diagnoses/risk factors were determined:', 'determined to have diagnoses/risk factors as follows:'])
        systemsReview += this.$custom.toListComma(systemItemTitles, null, 'lc')
      }
      if (this.medicalHistoryOther && this.medicalHistoryOther?.length > 0) {
        systemsReview += this.$custom.randomString(['in addition to', 'as well as', 'along with', 'and additionally', 'and in addition'])
        systemsReview += this.$custom.textAreaFlat(this.medicalHistoryOther)
      }
      systemsReview += ' '
      if (riskFactorTitles.length > 0) {
        systemsReview += this.$custom.randomString(['with', 'having', 'and'])
        systemsReview += this.$custom.randomString(['these', 'the following'])
        systemsReview += 'risk factors: '
        systemsReview += this.$custom.toListCommaAnd(riskFactorTitles, null, 'lc')
      }
      if (this.riskFactorOther && this.riskFactorOther?.length > 0) {
        if (riskFactorTitles.length > 0) {
          systemsReview += ', as well as '
        } else {
          systemsReview += 'with the following risk factors: '
        }
        systemsReview += this.$custom.textAreaFlat(this.riskFactorOther)
      }
      systemsReview += '.  '
      if (this.therapeuticAnticoagulant) {
        systemsReview += '  NOTE: PATIENT RECEIVING THERAPEUTIC ANTICOAGULANT.  '
      }
      this.systems_review = this.$custom.clean(systemsReview, 'Systems Review')
      this.$emit('input', this.systems_review)
    },
    */
  },
}
</script>
